/**
 * Merchandising order for PDP size / fit option values.
 *
 * Option values arrive in import/Admin order. This ranks display chips only —
 * callers must move DOM nodes and leave data-value-index / option indexes alone.
 *
 * A group is classified only when EVERY value matches that pattern (except fits,
 * where unknown values go last). Mixed or unrecognized groups are left untouched.
 */
(function (global) {
  'use strict';

  const NUMERIC_VALUE = /^[0-9]+([./-][0-9]+)*$/;
  // A number with a word qualifier, e.g. "37 Unhemmed" in a pant-length row.
  const QUALIFIED_NUMERIC_VALUE = /^[0-9]+(\.[0-9]+)?\s+[A-Za-z][A-Za-z ]*$/;
  const NUMERIC_LENGTH_VALUE =
    /^(\d+)\s*(S|R|L|SHORT|REGULAR|REG|LONG)$/i;
  const LENGTH_WORD_VALUE = /^(S|R|L|SHORT|REGULAR|REG|LONG)$/i;
  const ALPHA_SIZE_VALUE = /^(X{0,5}S|M|X{0,6}L|[2-9]XL)$/i;

  const LENGTH_RANK = {
    S: 0,
    SHORT: 0,
    R: 1,
    REG: 1,
    REGULAR: 1,
    L: 2,
    LONG: 2,
  };

  const PANT_FITS = ['tailored', 'slim-taper', 'slim', 'athletic', 'straight', 'classic'];
  const SHIRT_FITS = ['tailored', 'slim', 'standard'];
  const BLAZER_FITS = ['slim', 'standard', 'athletic'];

  const UNKNOWN_RANK = 10000;

  const handleizeFit = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-fit$/, '');

  const isFitOptionHandle = (handle) => String(handle || '').toLowerCase().includes('fit');

  const isColorOptionHandle = (handle) => {
    const normalized = String(handle || '').toLowerCase();
    return normalized === 'color' || normalized === 'colour' || normalized.includes('color');
  };

  const isNumericValue = (value) => NUMERIC_VALUE.test(String(value || '').trim());

  const isQualifiedNumericValue = (value) => QUALIFIED_NUMERIC_VALUE.test(String(value || '').trim());

  const isNumericLengthValue = (value) => NUMERIC_LENGTH_VALUE.test(String(value || '').trim());

  const isLengthWordValue = (value) => LENGTH_WORD_VALUE.test(String(value || '').trim());

  const isAlphaSizeValue = (value) => ALPHA_SIZE_VALUE.test(String(value || '').trim());

  const fitIndex = (value, order) => {
    const handle = handleizeFit(value);
    const index = order.indexOf(handle);
    return index;
  };

  const fitHits = (values, order) => values.filter((value) => fitIndex(value, order) >= 0).length;

  const resolveFitOrder = (handle, values) => {
    const h = String(handle || '').toLowerCase();
    if (h.includes('shirt')) return SHIRT_FITS;
    if (h.includes('blazer')) return BLAZER_FITS;
    if (h.includes('pant') || h.includes('chino') || h.includes('trouser')) return PANT_FITS;

    const scored = [
      { order: PANT_FITS, hits: fitHits(values, PANT_FITS) },
      { order: SHIRT_FITS, hits: fitHits(values, SHIRT_FITS) },
      { order: BLAZER_FITS, hits: fitHits(values, BLAZER_FITS) },
    ].sort((a, b) => b.hits - a.hits);

    if (scored[0].hits === 0) return PANT_FITS;
    if (scored[0].hits !== scored[1].hits) return scored[0].order;
    if (h.includes('short')) return PANT_FITS;
    return scored[0].order;
  };

  const looksLikeFitGroup = (values) => {
    if (
      values.some(
        (value) => isNumericValue(value) || isNumericLengthValue(value) || isAlphaSizeValue(value)
      )
    ) {
      return false;
    }
    return [PANT_FITS, SHIRT_FITS, BLAZER_FITS].some((order) => fitHits(values, order) === values.length);
  };

  const numericRank = (value) => {
    const parsed = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : UNKNOWN_RANK;
  };

  const numericLengthRank = (value) => {
    const match = String(value || '').trim().match(NUMERIC_LENGTH_VALUE);
    if (!match) return UNKNOWN_RANK;
    const waist = Number.parseInt(match[1], 10);
    const length = LENGTH_RANK[match[2].toUpperCase()];
    if (!Number.isFinite(waist) || length == null) return UNKNOWN_RANK;
    return waist * 4 + length;
  };

  const lengthWordRank = (value) => {
    const key = String(value || '').trim().toUpperCase();
    return Object.prototype.hasOwnProperty.call(LENGTH_RANK, key) ? LENGTH_RANK[key] : UNKNOWN_RANK;
  };

  const alphaSizeRank = (value) => {
    const token = String(value || '').trim().toUpperCase();
    if (token === 'M') return 100;

    const numbered = token.match(/^([2-9])XL$/);
    if (numbered) return 110 + Number(numbered[1]) * 10;

    const small = token.match(/^(X*)S$/);
    if (small) return 90 - small[1].length * 10;

    const large = token.match(/^(X*)L$/);
    if (large) return 110 + large[1].length * 10;

    return UNKNOWN_RANK;
  };

  const classifyOptionValues = (values, optionHandle) => {
    if (isColorOptionHandle(optionHandle)) return null;

    const trimmed = (values || []).map((value) => String(value || '').trim()).filter(Boolean);
    if (trimmed.length < 2) return null;

    if (isFitOptionHandle(optionHandle) || looksLikeFitGroup(trimmed)) {
      const fitOrder = resolveFitOrder(optionHandle, trimmed);
      // A *fit* handle with size-like values (rare) must fall through to size
      // ranking. Only treat the row as fits when at least one value is known.
      if (fitHits(trimmed, fitOrder) > 0 || looksLikeFitGroup(trimmed)) {
        return { kind: 'fit', fitOrder };
      }
    }

    if (trimmed.every(isNumericLengthValue)) return { kind: 'numeric-length' };
    if (trimmed.every((value) => isNumericValue(value) || isQualifiedNumericValue(value))) {
      return { kind: 'numeric' };
    }
    if (trimmed.every(isLengthWordValue)) return { kind: 'length-word' };
    if (trimmed.every(isAlphaSizeValue)) return { kind: 'alpha' };
    return null;
  };

  const rankOptionValue = (value, classification) => {
    if (!classification) return UNKNOWN_RANK;
    switch (classification.kind) {
      case 'fit': {
        const index = fitIndex(value, classification.fitOrder);
        return index === -1 ? UNKNOWN_RANK : index;
      }
      case 'numeric-length':
        return numericLengthRank(value);
      case 'numeric':
        return numericRank(value);
      case 'length-word':
        return lengthWordRank(value);
      case 'alpha':
        return alphaSizeRank(value);
      default:
        return UNKNOWN_RANK;
    }
  };

  const compareNumericLabels = (a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

  /**
   * Sort a list of entries in merchandising order.
   * @param {unknown[]} entries
   * @param {(entry: unknown) => string} getValue
   * @param {string} [optionHandle]
   * @returns {unknown[] | null} sorted entries, or null when the group should not move
   */
  const sortEntries = (entries, getValue, optionHandle) => {
    if (!Array.isArray(entries) || entries.length < 2 || typeof getValue !== 'function') return null;

    const values = entries.map((entry) => getValue(entry));
    const classification = classifyOptionValues(values, optionHandle);
    if (!classification) return null;

    const ranked = entries.map((entry, index) => ({
      entry,
      index,
      value: values[index],
      rank: rankOptionValue(values[index], classification),
    }));

    ranked.sort((a, b) => {
      // BSM-1161: keep localeCompare as the numeric sort. Stripping 32/34 to
      // 3234 (parseFloat) would put neck/waist ranges after 34.
      if (classification.kind === 'numeric') {
        return compareNumericLabels(a.value, b.value) || a.index - b.index;
      }
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.index - b.index;
    });

    if (ranked.every((item, index) => item.entry === entries[index])) return null;
    return ranked.map((item) => item.entry);
  };

  const sortValues = (values, optionHandle) => {
    const entries = (values || []).map((value, index) => ({ value, index }));
    const sorted = sortEntries(entries, (entry) => entry.value, optionHandle);
    return sorted ? sorted.map((entry) => entry.value) : null;
  };

  global.BonobosOptionValueOrder = {
    classifyOptionValues,
    rankOptionValue,
    sortEntries,
    sortValues,
  };
})(typeof globalThis !== 'undefined' ? globalThis : window);
